import { useTranslation } from 'react-i18next';
import { Button, Popover, Typography } from 'antd';
import { FlagFilled, InfoCircleOutlined } from '@ant-design/icons';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { GetIcon, WidgetLoader } from 'datastake-daf/dist/components';
import {
  getAssesment,
  InfoPopOverStyle,
  repeatObjects,
  riskColors,
  RiskScorePopover,
  RiskScorePopoverStyle,
} from '../../../helper';
import i18next from 'i18next';
import { assessmentOptions } from '../helper';
import useRiskGauge, { labels } from '../../../../../helpers/useRiskGauge';

const { Paragraph } = Typography;

const Expandable = ({ blue, text }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEllipsis, setIsElliptis] = useState(false);

  return (
    <div style={{ maxHeight: 98, overflowY: 'auto' }}>
      <Paragraph
        style={{ marginTop: 0, marginBottom: 0 }}
        ellipsis={isExpanded ? false : { rows: 3, expandable: false, onEllipsis: () => setIsElliptis(true) }}
      >
        <label style={{
          color: 'var(--mmt-primary-70)',
          marginBottom: 0,
        }}>{blue}</label>
        {' '}
        {text}
      </Paragraph>
      {isEllipsis && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            style={{ padding: 0 }}
            type="link"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? `${i18next.t('Show less')} -` : `${i18next.t('Show More')} +`}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function RiskScore({
  score = {
    totalRisks: 0,
    score: 0,
    risksWithScore: 0
  },
  canEdit,
  editDisabled = false,
  subScore = null,
  onEdit = () => { },
  riskScoreDate = null,
  subGroup,
  subSection,
  evolutionData = [],
  isRisk = false,
  isSubGroup = false,
}) {
  const container = useRef();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  const comments = useMemo(() => {
    if (evolutionData[0]) {
      if (evolutionData[evolutionData.length - 1].assessment) {
        return evolutionData[evolutionData.length - 1].assessment;
      }
    }
    return {};
  }, [evolutionData]);

  const getSubscoreAvg = () => {
    if (subScore && Object.keys(subScore).filter(key => !subScore[key]).length === 0) {
      return Object.values(subScore).reduce((a, b) => {
        return a + b;
      }, 0) / Object.keys(subScore).length;
    }
    return 0;
  }

  useRiskGauge({ container, score, subScore, showLabelInScore: true });

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  const getPopoverConfig = (key) => {
    const d = getLabelsAndDesc();
    const config = d[key];
    return config;
  };

  const getPopover = (key) => {
    const d = getLabelsAndDesc();
    const config = d[key];
    const comment = comments[`${key}Comment`];
    return config.label ? (
      <InfoPopOverStyle>
        <div className="header" style={{ paddingBottom: comment ? 8 : 0 }}>
          <h1>{config.label}</h1>
          {config.description && (
            <p>{config.description}</p>
          )}
        </div>
        {comment && (
          <Expandable blue={t('Comment: ')} text={comment} />
        )}
      </InfoPopOverStyle>
    ) : null;
  };

  const getLabelsAndDesc = () => {
    const options = assessmentOptions;
    let impactLabel = null;
    let threatLabel = null;
    let vulnerabillityLabel = null;
    let impactDesc = null;
    let threatDesc = null;
    let vulnerabillityDesc = null;
    if (options[subSection]) {
      if (options[subSection].all) {
        if (options[subSection][subGroup]) {
          if (options[subSection][subGroup].threat) {
            const th = options[subSection][subGroup].threat;
            const thO = th.find((v) => v.value === subScore.threat);
            if (thO) {
              if (thO.label) {
                threatLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                threatDesc = thO.description[i18next.language];
              }
            }
          } else {
            const th = options[subSection].all.threat;
            const thO = th.find((v) => v.value === subScore.threat);
            if (thO) {
              if (thO.label) {
                threatLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                threatDesc = thO.description[i18next.language];
              }
            }
          }

          if (options[subSection][subGroup].impact) {
            const th = options[subSection][subGroup].impact;
            const thO = th.find((v) => v.value === subScore.impact);
            if (thO) {
              if (thO.label) {
                impactLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                impactDesc = thO.description[i18next.language];
              }
            }
          } else {
            const th = options[subSection].all.impact;
            const thO = th.find((v) => v.value === subScore.impact);
            if (thO) {
              if (thO.label) {
                impactLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                impactDesc = thO.description[i18next.language];
              }
            }
          }

          if (options[subSection][subGroup].vulnerability) {
            const th = options[subSection][subGroup].vulnerability;
            const thO = th.find((v) => v.value === subScore.vulnerability);
            if (thO) {
              if (thO.label) {
                vulnerabillityLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                vulnerabillityDesc = thO.description[i18next.language];
              }
            }
          } else {
            const th = options[subSection].all.vulnerability;
            const thO = th.find((v) => v.value === subScore.vulnerability);
            if (thO) {
              if (thO.label) {
                vulnerabillityLabel = thO.label[i18next.language];
              }
              if (thO.description) {
                vulnerabillityDesc = thO.description[i18next.language];
              }
            }
          }
        }
      }
    }

    return {
      impact: {
        label: impactLabel,
        description: impactDesc,
      },
      threat: {
        label: threatLabel,
        description: threatDesc,
      },
      vulnerability: {
        label: vulnerabillityLabel,
        description: vulnerabillityDesc,
      },
    }
  }

  const getRiskScorePopover = () => {
    const mainScore = subScore ? getSubscoreAvg() : score.score;
    const s = Math.floor(mainScore);
    const color = riskColors[s];
    const label = labels[s];
    const d = getLabelsAndDesc();

    return subScore ? (
      <RiskScorePopoverStyle>
        {riskScoreDate && (
          <span className="date">
            {riskScoreDate.firstDate.format('DD MMM YYYY')}
            {' '}
            -
            {' '}
            {riskScoreDate.lastDate ?
              riskScoreDate.lastDate.format('DD MMM YYYY')
              : i18next.t('Current')
            }
          </span>
        )}
        <h2>{t('Risk score')}</h2>
        <div className="risk-score">
          <div className="info" style={{ color }}>
            <FlagFilled
              style={{ fontSize: 14 }}
            />
            <p>{label}</p>
          </div>
          <div className="score">
            <p>{mainScore.toFixed(2)}</p>
          </div>
        </div>
        {Object.values(subScore).filter((s) => s).length > 0 && (
          <>
            <h2>{t('Breakdown')}</h2>
            <div className="breakdown">
              {repeatObjects(subScore, key => getAssesment(
                key,
                subScore[key],
                (s) => i18next.t(s),
                true,
                d,
              ))}
            </div>
          </>
        )}
      </RiskScorePopoverStyle>
    ) : null;
  };

  if (isSubGroup) {
    return (
      <div className="list">
        {subScore ? repeatObjects(subScore, key => (
          <div key={key} className={`sub-group group-${key}`}>
            <h1 style={{ marginBottom: 0 }}>{t(key)}</h1>
            <div className="levels">
              {[4, 3, 2, 1, 0].map((index) => (
                <svg
                  key={index}
                  width="11"
                  height="11"
                  viewBox="0 0 8 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 4L6 7.4641L2 7.4641L-1.74846e-07 4L2 0.535898L6 0.535898L8 4Z"
                    fill={(index + 1) <= subScore[key] ? riskColors[subScore[key]] : '#f3f3f3'}
                  />
                </svg>
              ))}
            </div>
            <p>{getPopoverConfig(key).label}</p>
          </div>
        )) : null}
      </div>
    );
  }

  return (
    <div className="riskProfile risk-widget" style={{ display: 'flex', flexDirection: 'column' }}>
      {loading && <WidgetLoader background="white" />}
      <div className="title">
        <div>
          <GetIcon icon="star" type="line" style={{ color: '#BDBDBD' }} />
          <h1 className="risk-h1">{i18next.t('Risk Score')}</h1>
        </div>
        {subScore && canEdit ? <Button
          type="link"
          icon={<GetIcon icon="FormOutlined" />}
          disabled={editDisabled}
          onClick={onEdit}>
          {i18next.t('Edit')}
        </Button> : null}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <RiskScorePopover
          content={getRiskScorePopover}
          renderPopover={!!subScore}
        >
          <div>
            <div
              ref={container}
              style={{
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                maxHeight: '160px',
                padding: '12px'
              }}>
            </div>
          </div>
        </RiskScorePopover>
        <div className="subScores">
          {subScore ? repeatObjects(subScore, key => (
            <div key={key} className="subScore mt-2">
              <div style={{ display: 'flex', marginBottom: 4 }}>
                <p style={{ marginBottom: 0 }}>{t(key)}</p>
                {getPopover(key) ? (
                  <Popover
                    placement="bottomLeft"
                    title={null}
                    content={getPopover(key)}
                  >
                    <div style={{ marginTop: -2, marginLeft: 4, color: '#868686' }}>
                      <InfoCircleOutlined
                        style={{ fontSize: 12 }}
                      />
                    </div>
                  </Popover>
                ) : (
                  <div style={{ marginTop: -2, marginLeft: 4, color: '#868686' }}>
                    <InfoCircleOutlined
                      style={{ fontSize: 12 }}
                    />
                  </div>
                )}
              </div>
              <div className="levels">
                {[...Array(5)].map((level, index) => (
                  <div key={index} style={{
                    background: (index + 1) <= subScore[key]
                      ? riskColors[subScore[key]] : '#f3f3f3'
                  }}></div>
                ))}
              </div>
            </div>
          )) : (
            score.score ? <div className="subScore mt-3">
              <span style={{ fontSize: '16px' }}><b style={{
                color: "#001529",
                fontWeight: 900,
                fontVariant: 'normal',
                fontSize: '16px'
              }}>{score.risksWithScore}/{score.totalRisks}</b> {i18next.t('risk types assessed')}</span>
            </div> : null)}

        </div>
        {(!subScore && !isRisk) ? <h1 className="riskScore mt-2">{i18next.t('Aggregate Risk')}: {labels[Math.round(score.score)]}</h1> : null}
      </div>
    </div>
  );
}
